import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Paper,
  Stack,
  Typography,
} from "@mui/material";
import {
  DeleteOutline,
  DownloadOutlined,
  UploadFileOutlined,
} from "@mui/icons-material";
import { DataGrid } from "@mui/x-data-grid";
import * as XLSX from "xlsx";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { getDate as getIndiaDate } from "../../utils/reportUtils";
import LatestUploadCard from "../LatestUploadCard";

const API = process.env.REACT_APP_BACKEND_URL;

const COMBINED_COLUMNS = [
  { header: "Material", field: "material" },
  { header: "Description", field: "description" },
  { header: "Plant", field: "plant" },
  { header: "MRP Element", field: "element" },
  { header: "Reqmt Date", field: "reqDate", type: "date" },
  { header: "Reqmt Qty", field: "reqQty", type: "number" },
  { header: "MRP BUn", field: "mrpUnit" },
  { header: "Material Group", field: "group" },
  { header: "SLoc", field: "storageLocation" },
  { header: "Unrestricted", field: "unrestricted", type: "number" },
  { header: "Stock BUn", field: "stockUnit" },
  { header: "Stock Date", field: "stockDate", type: "date" },
];

const PR_COLUMNS = [
  { header: "Plant", field: "plant", required: true },
  { header: "Storage location", field: "storageLocation" },
  { header: "Purchasing Group", field: "purchasingGroup" },
  { header: "Deletion indicator", field: "deletionIndicator" },
  { header: "Requisition Date", field: "requisitionDate", type: "date" },
  { header: "Release Date", field: "releaseDate", type: "date" },
  {
    header: "Purchase Requisition",
    field: "purchaseRequisition",
    required: true,
  },
  { header: "Item of requisition", field: "item" },
  { header: "Material", field: "material", required: true },
  { header: "Short Text", field: "shortText" },
  { header: "Quantity requested", field: "quantityRequested", type: "number" },
  { header: "Quantity ordered", field: "quantityOrdered", type: "number" },
  { header: "Unit of Measure", field: "unit" },
  { header: "Release indicator", field: "releaseIndicator" },
  { header: "Deliv. date(From/to)", field: "deliveryDate", type: "date" },
  { header: "Name of Supplier", field: "nameOfSupplier" },
  { header: "Fixed vendor", field: "fixedVendor" },
  { header: "Supplier", field: "supplier" },
  { header: "Document Type", field: "documentType" },
  { header: "Processing status", field: "processingStatus" },
  { header: "Creation indicator", field: "creationIndicator" },
  { header: "Release strategy", field: "releaseStrategy" },
  { header: "Acct Assignment Cat.", field: "accountAssignmentCategory" },
  { header: "Purchasing info rec.", field: "purchasingInfoRecord" },
  { header: "Purchase order", field: "purchaseOrder" },
  { header: "Special Stock", field: "specialStock" },
  {
    header: "Purchasing group Description",
    field: "purchasingGroupDescription",
  },
  { header: "Item Category", field: "itemCategory" },
];

const PO_COLUMNS = [
  { header: "Release indicator", field: "releaseIndicator" },
  { header: "Release Strategy", field: "releaseStrategy" },
  {
    header: "Purchasing Document",
    field: "purchasingDocument",
    required: true,
  },
  { header: "Document Date", field: "documentDate", type: "date" },
  { header: "Purchasing Doc. Type", field: "purchasingDocType" },
  { header: "Purch. doc. category", field: "purchasingDocCategory" },
  { header: "Purchasing Group", field: "purchasingGroup" },
  { header: "Supplier/Supplying Plant", field: "supplierSupplyingPlant" },
  { header: "Material", field: "material", required: true },
  { header: "Item", field: "item" },
  { header: "Short Text", field: "shortText" },
  { header: "Material Group", field: "materialGroup" },
  { header: "Deletion indicator", field: "deletionIndicator" },
  { header: "Item Category", field: "itemCategory" },
  { header: "Acct Assignment Cat.", field: "accountAssignmentCategory" },
  { header: "Plant", field: "plant", required: true },
  { header: "Storage location", field: "storageLocation" },
  { header: "Order Quantity", field: "orderQuantity", type: "number" },
  { header: "Order Unit", field: "orderUnit" },
  { header: "Quantity in SKU", field: "quantityInSku", type: "number" },
  { header: "Stockkeeping unit", field: "stockkeepingUnit" },
  { header: "Net Price", field: "netPrice", type: "number" },
  { header: "Currency", field: "currency" },
  { header: "Tax code", field: "taxCode" },
  { header: "Net Order Value", field: "netOrderValue", type: "number" },
  { header: "Price unit", field: "priceUnit", type: "number" },
  { header: "Target Quantity", field: "targetQuantity", type: "number" },
  {
    header: "Open Target Quantity",
    field: "openTargetQuantity",
    type: "number",
  },
  {
    header: "Still to be delivered (qty)",
    field: "stillToBeDeliveredQty",
    type: "number",
  },
  {
    header: "Still to be delivered (value)",
    field: "stillToBeDeliveredValue",
    type: "number",
  },
  {
    header: "Still to be invoiced (qty)",
    field: "stillToBeInvoicedQty",
    type: "number",
  },
  {
    header: "Still to be invoiced (val.)",
    field: "stillToBeInvoicedValue",
    type: "number",
  },
  { header: "No. of Positions", field: "numberOfPositions", type: "number" },
  { header: "Release group", field: "releaseGroup" },
  { header: "Release State", field: "releaseState" },
  { header: "Created by", field: "createdBy" },
  { header: "Created on", field: "createdOn", type: "date" },
  { header: "Version", field: "version" },
  { header: "Release option", field: "releaseOption" },
  { header: "Requested by", field: "requestedBy" },
  { header: "Short Text", field: "changeShortText" },
  { header: "Reason for change", field: "reasonForChange" },
  { header: "Net Order Value", field: "changedNetOrderValue", type: "number" },
  { header: "Difference Amount", field: "differenceAmount", type: "number" },
];

const DELIVERY_COLUMNS = [
  { header: "Material Document", field: "materialDocument" },
  { header: "Posting Date", field: "postingDate", type: "date" },
  { header: "Storage Bin", field: "storageBin" },
  { header: "Movement type", field: "movementType" },
  { header: "Material", field: "material", required: true },
  { header: "Material description", field: "materialDescription" },
  { header: "Unit of Entry", field: "unitOfEntry" },
  {
    header: "Qty in unit of entry",
    field: "quantityInUnitOfEntry",
    type: "number",
    required: true,
  },
  { header: "Amt.in loc.cur.", field: "amountInLocalCurrency", type: "number" },
  { header: "Storage location", field: "storageLocation" },
  { header: "User Name", field: "userName" },
  { header: "Time of Entry", field: "timeOfEntry" },
  { header: "Order", field: "orderNumber" },
  { header: "Vendor Name", field: "vendorName" },
  { header: "Purchase order", field: "purchaseOrder", required: true },
  { header: "Vendor", field: "vendor" },
  { header: "Reference", field: "reference" },
  { header: "Plant", field: "plant", required: true },
  { header: "Document Date", field: "documentDate", type: "date" },
  { header: "Purchasing Group", field: "purchasingGroup" },
  { header: "Material Doc.Item", field: "materialDocumentItem" },
  { header: "Document Header Text", field: "documentHeaderText" },
];

const normalize = (value) =>
  value === null || value === undefined ? "" : String(value).trim();

const isBlank = (value) =>
  value === null || value === undefined || value === "";

// Returns null for a blank cell and NaN for text that is not a number (reported as a row error).
// Handles SAP formats: trailing minus "100-", brackets "(100)" and "1.234,5" decimal commas.
const toNumber = (value) => {
  if (isBlank(value)) return null;
  if (typeof value === "number") return Number.isFinite(value) ? value : NaN;

  let text = String(value).replace(/\s/g, "");
  if (!text) return null;

  let negative = false;

  if (/^\(.*\)$/.test(text)) {
    negative = true;
    text = text.slice(1, -1);
  }

  if (text.endsWith("-")) {
    negative = !negative;
    text = text.slice(0, -1);
  }

  if (
    text.includes(",") &&
    text.includes(".") &&
    text.lastIndexOf(",") > text.lastIndexOf(".")
  ) {
    text = text.replace(/\./g, "").replace(",", ".");
  } else {
    text = text.replace(/,/g, "");
  }

  if (!/^[-+]?(\d+\.?\d*|\.\d+)(e[-+]?\d+)?$/i.test(text)) return NaN;

  const number = Number(text);
  return negative ? -number : number;
};

const makeUTCDate = (year, month, day) => {
  const date = new Date(Date.UTC(year, month - 1, day, 12, 0, 0));

  if (
    date.getUTCFullYear() !== year ||
    date.getUTCMonth() !== month - 1 ||
    date.getUTCDate() !== day
  ) {
    return null;
  }

  return date;
};

const excelDateToDate = (value, date1904 = false) => {
  if (isBlank(value)) return null;

  if (value instanceof Date && !Number.isNaN(value.getTime())) {
    return makeUTCDate(
      value.getUTCFullYear(),
      value.getUTCMonth() + 1,
      value.getUTCDate(),
    );
  }

  if (typeof value === "number") {
    // Workbooks using the 1904 date system store serials 1462 days lower.
    const parsed = XLSX.SSF.parse_date_code(value, { date1904 });
    return parsed ? makeUTCDate(parsed.y, parsed.m, parsed.d) : null;
  }

  const text = String(value).trim();
  const dmy = text.match(/^(\d{1,2})[-/.](\d{1,2})[-/.](\d{2,4})$/);

  if (dmy) {
    let day = Number(dmy[1]);
    let month = Number(dmy[2]);
    let year = Number(dmy[3]);
    if (year < 100) year += 2000;

    // DD/MM is the default; a second part above 12 can only be MM/DD (e.g. 02/13/2026).
    if (month > 12 && day <= 12) {
      [day, month] = [month, day];
    }

    return makeUTCDate(year, month, day);
  }

  // Parse date-only ISO values from their components, not local time.
  const iso = text.match(/^(\d{4})[-/](\d{1,2})[-/](\d{1,2})(?:$|[T\s])/);
  if (iso) {
    return makeUTCDate(Number(iso[1]), Number(iso[2]), Number(iso[3]));
  }

  // Other text dates (e.g. "15-Jan-2026") are parsed as local dates, so read them back
  // with local getters; UTC getters would give the previous day in India.
  const parsed = new Date(text);
  if (Number.isNaN(parsed.getTime())) return null;

  return makeUTCDate(
    parsed.getFullYear(),
    parsed.getMonth() + 1,
    parsed.getDate(),
  );
};

const formatDate = (value) => {
  if (isBlank(value)) return "—";

  const date = value instanceof Date ? value : excelDateToDate(value);
  if (!date || Number.isNaN(date.getTime())) return String(value);

  return `${String(date.getUTCDate()).padStart(2, "0")}-${String(
    date.getUTCMonth() + 1,
  ).padStart(2, "0")}-${date.getUTCFullYear()}`;
};

const displayValue = (value, column) => {
  if (isBlank(value)) return "—";
  if (column.type === "date") return formatDate(value);
  return String(value);
};

const makeTemplate = (columns, sheetName, fileName) => {
  const headers = columns.map((column) => column.header);
  const worksheet = XLSX.utils.aoa_to_sheet([headers]);
  worksheet["!cols"] = columns.map((column) => ({
    wch:
      column.header.length > 24 ? 26 : Math.max(14, column.header.length + 2),
  }));
  const workbook = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(workbook, worksheet, sheetName);
  XLSX.writeFile(workbook, fileName);
};

const getAuthConfig = () => {
  const storedUser = JSON.parse(localStorage.getItem("user") || "null");
  if (!storedUser?.accessToken) throw new Error("You are not logged in.");
  return { headers: { Authorization: `Bearer ${storedUser.accessToken}` } };
};

// The most recent file name and upload time among rows that record their source file.
const getLatestSource = (rows) => {
  let latest = null;
  rows.forEach((row) => {
    if (!row.sourceUploadedAt) return;
    if (
      !latest ||
      new Date(row.sourceUploadedAt) > new Date(latest.sourceUploadedAt)
    ) {
      latest = row;
    }
  });
  return {
    fileName: latest?.sourceFileName || null,
    uploadedAt: latest?.sourceUploadedAt || null,
  };
};

const countUnique = (rows, field) =>
  new Set(rows.map((row) => normalize(row[field])).filter(Boolean)).size;

const fetchLatestRows = async (endpoint, authConfig) => {
  const response = await axios.get(`${API}${endpoint}`, authConfig);
  return Array.isArray(response.data) ? response.data : [];
};

const COMBINED_CONFIG = {
  title: "Combined MRP and Stock Report",
  description:
    "Upload the combined report using the original template columns A to L. Open PR, Open PO, and Delivery reports are uploaded in their own tabs.",
  columns: COMBINED_COLUMNS,
  requiredFields: ["material", "plant"],
  templateName: "ProcureTrack_Combined_Report_Template.xlsx",
  sheetName: "Combined Report",
  buildPayload: (rows, fileName) => {
    const hasAny = (row, fields) =>
      fields.some((field) => !isBlank(row[field]));
    const mrpFields = ["element", "reqDate", "reqQty", "mrpUnit", "group"];
    const stockFields = [
      "storageLocation",
      "unrestricted",
      "stockUnit",
      "stockDate",
    ];

    const mrpReports = rows
      .filter((row) => row.material && row.plant && hasAny(row, mrpFields))
      .map((row) => ({
        material: row.material,
        description: row.description,
        plant: row.plant,
        element: row.element,
        reqDate: row.reqDate || null,
        reqQty: row.reqQty,
        unit: row.mrpUnit,
        materialGroup: row.group,
      }));

    const stockReports = rows
      .filter((row) => row.material && row.plant && hasAny(row, stockFields))
      .map((row) => ({
        material: row.material,
        description: row.description,
        plant: row.plant,
        storageLocation: row.storageLocation,
        unrestricted: row.unrestricted,
        unit: row.stockUnit,
        stockDate: row.stockDate || null,
      }));

    if (!mrpReports.length && !stockReports.length) {
      throw new Error("No MRP or Stock data was found to upload.");
    }

    return {
      endpoint: "/report-upload",
      // Combined upload updates only MRP and Stock.
      // Omit PR/PO fields so this upload cannot be mistaken for an empty PR/PO snapshot.
      body: { fileName, mrpReports, stockReports },
    };
  },
  // MRP and Stock rows are shown in the same column layout as the uploaded file.
  loadLatest: async (authConfig) => {
    const [upload, mrpRows, stockRows] = await Promise.all([
      axios
        .get(`${API}/report-upload/latest`, authConfig)
        .then((response) => response.data)
        .catch(() => null),
      fetchLatestRows("/mrp/latest", authConfig),
      fetchLatestRows("/stock/latest", authConfig),
    ]);
    return {
      fileName: upload?.fileName || null,
      uploadedAt: upload?.uploadDate || upload?.createdDate || null,
      uploadedBy: upload?.orgUserName || null,
      stats: [
        { label: "MRP lines", value: mrpRows.length },
        { label: "Stock lines", value: stockRows.length },
        {
          label: "Materials",
          value: countUnique([...mrpRows, ...stockRows], "material"),
        },
        {
          label: "Plants",
          value: countUnique([...mrpRows, ...stockRows], "plant"),
        },
      ],
      rows: [
        ...mrpRows.map((row) => ({
          material: row.material,
          description: row.description,
          plant: row.plant,
          element: row.element,
          reqDate: row.reqDate,
          reqQty: row.reqQty,
          mrpUnit: row.unit,
          group: row.materialGroup,
        })),
        ...stockRows.map((row) => ({
          material: row.material,
          description: row.description,
          plant: row.plant,
          storageLocation: row.storageLocation,
          unrestricted: row.unrestricted,
          stockUnit: row.unit,
          stockDate: row.stockDate,
        })),
      ],
    };
  },
};

const PR_CONFIG = {
  title: "Open PR Report",
  description:
    "Upload the current Open Purchase Requisition report. PRs with a Purchase Order number are treated as converted/closed by the existing planning logic.",
  columns: PR_COLUMNS,
  requiredFields: ["purchaseRequisition", "material", "plant"],
  templateName: "ProcureTrack_Open_PR_Template.xlsx",
  sheetName: "Open PR",
  buildPayload: (rows, fileName) => ({
    endpoint: `/open-pr/upload?fileName=${encodeURIComponent(fileName)}`,
    body: rows.map((row) => ({
      plant: row.plant,
      storageLocation: row.storageLocation,
      purchasingGroup: row.purchasingGroup,
      deletionIndicator: row.deletionIndicator,
      requisitionDate: row.requisitionDate || null,
      releaseDate: row.releaseDate || null,
      purchaseRequisition: row.purchaseRequisition,
      itemOfRequisition: row.item,
      material: row.material,
      shortText: row.shortText,
      quantityRequested: row.quantityRequested,
      quantityOrdered: row.quantityOrdered,
      unitOfMeasure: row.unit,
      releaseIndicator: row.releaseIndicator,
      deliveryDate: row.deliveryDate || null,
      nameOfSupplier: row.nameOfSupplier,
      fixedVendor: row.fixedVendor,
      supplier: row.supplier,
      documentType: row.documentType,
      processingStatus: row.processingStatus,
      creationIndicator: row.creationIndicator,
      releaseStrategy: row.releaseStrategy,
      accountAssignmentCategory: row.accountAssignmentCategory,
      purchasingInfoRecord: row.purchasingInfoRecord,
      purchaseOrder: row.purchaseOrder,
      specialStock: row.specialStock,
      purchasingGroupDescription: row.purchasingGroupDescription,
      itemCategory: row.itemCategory,
      item: row.item,
      unit: row.unit,
    })),
  }),
  loadLatest: async (authConfig) => {
    const rows = await fetchLatestRows("/open-pr/latest", authConfig);
    return {
      ...getLatestSource(rows),
      stats: [
        { label: "PR lines", value: rows.length },
        {
          label: "Open PR lines",
          value: rows.filter((row) => row.status === "OPEN").length,
        },
        {
          label: "Requisitions",
          value: countUnique(rows, "purchaseRequisition"),
        },
        { label: "Materials", value: countUnique(rows, "material") },
      ],
      rows: rows.map((row) => ({
        ...row,
        item: row.item || row.itemOfRequisition,
        unit: row.unitOfMeasure || row.unit,
      })),
    };
  },
};

const PO_CONFIG = {
  title: "Open PO Report",
  description:
    "Upload the current Open Purchase Order report. The received quantity and latest receipt date are calculated from the separate Delivery Report page.",
  columns: PO_COLUMNS,
  requiredFields: ["purchasingDocument", "material", "plant"],
  templateName: "ProcureTrack_Open_PO_Template.xlsx",
  sheetName: "Open PO",
  buildPayload: (rows, fileName) => ({
    endpoint: `/open-po/upload?fileName=${encodeURIComponent(fileName)}`,
    body: rows.map((row) => ({
      releaseIndicator: row.releaseIndicator,
      releaseStrategy: row.releaseStrategy,
      purchasingDocument: row.purchasingDocument,
      documentDate: row.documentDate || null,
      purchasingDocType: row.purchasingDocType,
      purchasingDocCategory: row.purchasingDocCategory,
      purchasingGroup: row.purchasingGroup,
      supplierSupplyingPlant: row.supplierSupplyingPlant,
      material: row.material,
      item: row.item,
      shortText: row.shortText,
      materialGroup: row.materialGroup,
      deletionIndicator: row.deletionIndicator,
      plant: row.plant,
      storageLocation: row.storageLocation,
      orderQuantity: row.orderQuantity,
      orderUnit: row.orderUnit,
      quantityInSku: row.quantityInSku,
      stockkeepingUnit: row.stockkeepingUnit,
      netPrice: row.netPrice,
      currency: row.currency,
      taxCode: row.taxCode,
      netOrderValue: row.netOrderValue,
      priceUnit: row.priceUnit,
      targetQuantity: row.targetQuantity,
      openTargetQuantity: row.openTargetQuantity,
      stillToBeDeliveredQty: row.stillToBeDeliveredQty,
      stillToBeDeliveredValue: row.stillToBeDeliveredValue,
      stillToBeInvoicedQty: row.stillToBeInvoicedQty,
      stillToBeInvoicedValue: row.stillToBeInvoicedValue,
      numberOfPositions: row.numberOfPositions,
      releaseGroup: row.releaseGroup,
      releaseState: row.releaseState,
      createdBy: row.createdBy,
      createdOn: row.createdOn || null,
      version: row.version,
      releaseOption: row.releaseOption,
      requestedBy: row.requestedBy,
      changeShortText: row.changeShortText,
      reasonForChange: row.reasonForChange,
      changedNetOrderValue: row.changedNetOrderValue,
      differenceAmount: row.differenceAmount,
      vendor: row.vendor,
      vendorName: row.supplierSupplyingPlant,
      unit: row.orderUnit,
      quantityDelivered: null,
    })),
  }),
  loadLatest: async (authConfig) => {
    const rows = await fetchLatestRows("/open-po/latest", authConfig);
    return {
      ...getLatestSource(rows),
      stats: [
        { label: "PO lines", value: rows.length },
        {
          label: "Purchase orders",
          value: countUnique(rows, "purchasingDocument"),
        },
        { label: "Suppliers", value: countUnique(rows, "vendorName") },
        { label: "Materials", value: countUnique(rows, "material") },
      ],
      rows,
    };
  },
};

const DELIVERY_CONFIG = {
  title: "Delivery Report",
  description:
    "Upload the SAP material movement/delivery report. Qty in unit of entry is used as the actual received quantity, and Posting Date is used as the receipt date.",
  columns: DELIVERY_COLUMNS,
  requiredFields: [
    "purchaseOrder",
    "material",
    "plant",
    "quantityInUnitOfEntry",
  ],
  templateName: "ProcureTrack_Delivery_Report_Template.xlsx",
  sheetName: "Delivery Report",
  buildPayload: (rows, fileName) => ({
    endpoint: `/delivery-report/upload?fileName=${encodeURIComponent(fileName)}`,
    body: rows.map((row) => ({
      materialDocument: row.materialDocument,
      postingDate: row.postingDate || null,
      storageBin: row.storageBin,
      movementType: row.movementType,
      material: row.material,
      materialDescription: row.materialDescription,
      unitOfEntry: row.unitOfEntry,
      quantityInUnitOfEntry: row.quantityInUnitOfEntry,
      amountInLocalCurrency: row.amountInLocalCurrency,
      storageLocation: row.storageLocation,
      userName: row.userName,
      timeOfEntry: row.timeOfEntry,
      orderNumber: row.orderNumber,
      vendorName: row.vendorName,
      purchaseOrder: row.purchaseOrder,
      vendor: row.vendor,
      reference: row.reference,
      plant: row.plant,
      documentDate: row.documentDate || null,
      purchasingGroup: row.purchasingGroup,
      materialDocumentItem: row.materialDocumentItem,
      documentHeaderText: row.documentHeaderText,
    })),
  }),
  loadLatest: async (authConfig) => {
    const rows = await fetchLatestRows("/delivery-report/latest", authConfig);
    return {
      ...getLatestSource(rows),
      stats: [
        { label: "Receipt lines", value: rows.length },
        {
          label: "Purchase orders",
          value: countUnique(rows, "purchaseOrder"),
        },
        { label: "Suppliers", value: countUnique(rows, "vendorName") },
        { label: "Materials", value: countUnique(rows, "material") },
      ],
      rows,
    };
  },
};

const EMPTY_LATEST = {
  rows: [],
  fileName: null,
  uploadedAt: null,
  uploadedBy: null,
  stats: [],
};

function ExcelUploadPanel({ config }) {
  const fileInputRef = useRef(null);
  const [file, setFile] = useState(null);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [latest, setLatest] = useState(EMPTY_LATEST);
  const [latestLoading, setLatestLoading] = useState(true);
  const [latestError, setLatestError] = useState("");
  const navigate = useNavigate();

  const loadLatest = async () => {
    try {
      setLatestLoading(true);
      setLatestError("");
      const result = await config.loadLatest(getAuthConfig());
      const dateColumns = config.columns.filter(
        (column) => column.type === "date",
      );
      setLatest({
        fileName: result.fileName,
        uploadedAt: result.uploadedAt,
        uploadedBy: result.uploadedBy || null,
        stats: result.stats || [],
        rows: result.rows.map((row, index) => {
          const next = { ...row, id: index + 1 };
          // Backend dates are India-time timestamps; keep only the India date.
          dateColumns.forEach((column) => {
            next[column.field] = getIndiaDate(row[column.field]);
          });
          return next;
        }),
      });
    } catch (latestLoadError) {
      setLatest(EMPTY_LATEST);
      setLatestError(
        latestLoadError?.response?.data?.Exception ||
          latestLoadError?.message ||
          "Unable to load the latest uploaded data.",
      );
    } finally {
      setLatestLoading(false);
    }
  };

  useEffect(() => {
    loadLatest();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [config]);

  const clear = () => {
    setFile(null);
    setRows([]);
    setError("");
    setSuccess("");
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const handleDownloadTemplate = () => {
    setError("");
    setSuccess("");
    makeTemplate(config.columns, config.sheetName, config.templateName);
    setSuccess(`${config.title} template downloaded.`);
  };

  const handleFileChange = async (event) => {
    const selectedFile = event.target.files?.[0];
    if (!selectedFile) return;

    setError("");
    setSuccess("");
    setRows([]);
    setFile(null);
    setLoading(true);

    try {
      const lowerName = selectedFile.name.toLowerCase();
      if (!lowerName.endsWith(".xlsx") && !lowerName.endsWith(".xls")) {
        throw new Error("Please upload an Excel file (.xlsx or .xls).");
      }

      const workbook = XLSX.read(await selectedFile.arrayBuffer(), {
        type: "array",
      });
      if (!workbook.SheetNames.length) {
        throw new Error("The Excel file does not contain a sheet.");
      }

      const worksheet = workbook.Sheets[workbook.SheetNames[0]];
      const headerRows = XLSX.utils.sheet_to_json(worksheet, {
        header: 1,
        defval: "",
        raw: true,
      });
      if (!headerRows.length)
        throw new Error("The uploaded Excel sheet is empty.");

      const actualHeaders = headerRows[0].map(normalize);
      const expectedHeaders = config.columns.map((column) => column.header);
      const usedHeaderCounts = {};
      const columnIndices = expectedHeaders.map((header) => {
        const occurrence = usedHeaderCounts[header] || 0;
        usedHeaderCounts[header] = occurrence + 1;

        const matchingIndices = [];
        actualHeaders.forEach((actualHeader, index) => {
          if (actualHeader === header) matchingIndices.push(index);
        });

        return matchingIndices[occurrence] ?? -1;
      });

      const missingHeaders = expectedHeaders.filter(
        (header, index) => columnIndices[index] === -1,
      );
      if (missingHeaders.length) {
        throw new Error(
          `The Excel file is missing these columns (or duplicate occurrences): ${missingHeaders.join(", ")}. Download the template to get the exact headings.`,
        );
      }

      const rawRows = headerRows.slice(1);
      if (!rawRows.length)
        throw new Error("The Excel file contains headers but no data rows.");

      const date1904 = Boolean(workbook.Workbook?.WBProps?.date1904);
      // Cells that have a value which is not a valid number/date, reported with the Excel row number
      const invalidCells = [];

      const convertedRows = rawRows
        .map((excelRow, index) => {
          const row = { id: index + 1 };
          config.columns.forEach((column, columnIndex) => {
            const value = excelRow[columnIndices[columnIndex]];
            if (column.type === "date") {
              const date = excelDateToDate(value, date1904);
              if (!date && normalize(value)) {
                invalidCells.push(
                  `Row ${index + 2}: ${column.header} "${normalize(value)}" is not a valid date.`,
                );
              }
              row[column.field] = date;
            } else if (column.type === "number") {
              const number = toNumber(value);
              if (Number.isNaN(number)) {
                invalidCells.push(
                  `Row ${index + 2}: ${column.header} "${normalize(value)}" is not a valid number.`,
                );
              }
              row[column.field] = Number.isNaN(number) ? null : number;
            } else row[column.field] = normalize(value);
          });
          return row;
        })
        .filter((row) =>
          config.columns.some((column) => !isBlank(row[column.field])),
        );

      if (!convertedRows.length)
        throw new Error("The Excel file does not contain any data rows.");

      const validationErrors = [...invalidCells];
      convertedRows.forEach((row, index) => {
        config.requiredFields.forEach((field) => {
          if (isBlank(row[field])) {
            const column = config.columns.find((item) => item.field === field);
            validationErrors.push(
              `Row ${index + 2}: ${column?.header || field} is required.`,
            );
          }
        });
      });
      if (validationErrors.length) {
        throw new Error(validationErrors.slice(0, 15).join("\n"));
      }

      setFile(selectedFile);
      setRows(convertedRows);
      setSuccess(
        `${convertedRows.length} ${config.title.toLowerCase()} row(s) loaded successfully.`,
      );
    } catch (uploadError) {
      setError(uploadError?.message || "Unable to read the Excel file.");
    } finally {
      setLoading(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  const handleSubmit = async () => {
    if (!file || !rows.length) {
      setError(`Please choose a ${config.title} Excel file first.`);
      return;
    }

    setError("");
    setSuccess("");
    setUploading(true);

    try {
      const authConfig = getAuthConfig();
      const result = config.buildPayload(rows, file.name);
      const response = await axios.post(
        `${API}${result.endpoint}`,
        result.body,
        authConfig,
      );
      const message =
        typeof response.data === "string"
          ? response.data
          : `Reports uploaded successfully. ${response.data?.recordCount ?? 0} record(s) created.`;
      setSuccess(message);
      if (config === COMBINED_CONFIG) {
        navigate("/dashboard");
        return;
      }
      // Show what is now stored instead of the submitted preview.
      setFile(null);
      setRows([]);
      await loadLatest();
    } catch (uploadError) {
      setError(
        uploadError?.response?.data?.Exception ||
          uploadError?.response?.data?.message ||
          uploadError?.response?.data ||
          uploadError?.message ||
          "Report upload failed.",
      );
    } finally {
      setUploading(false);
    }
  };

  const gridColumns = useMemo(
    () =>
      config.columns.map((column) => ({
        field: column.field,
        headerName: column.header,
        minWidth: column.header.length > 22 ? 180 : 130,
        flex: column.header.length > 22 ? 1.5 : 1,
        valueFormatter: (value) => displayValue(value, column),
      })),
    [config],
  );

  return (
    <Box>
      {(error || success) && (
        <Alert
          severity={error ? "error" : "success"}
          sx={{ mb: 2, whiteSpace: "pre-line" }}
          onClose={() => {
            setError("");
            setSuccess("");
          }}
        >
          {error || success}
        </Alert>
      )}

      <Box sx={{ mb: 3 }}>
        <LatestUploadCard
          fileName={latest.fileName}
          uploadedAt={latest.uploadedAt}
          uploadedBy={latest.uploadedBy}
          stats={latest.stats}
          loading={latestLoading}
          error={latestError}
          isEmpty={latest.rows.length === 0}
          emptyMessage={`No ${config.title.toLowerCase()} has been uploaded yet.`}
          onRefresh={loadLatest}
          rows={latest.rows}
          columns={gridColumns}
        />
      </Box>

      <Paper
        elevation={0}
        sx={{ p: 3, border: "1px solid #e3e7ef", borderRadius: 2, mb: 3 }}
      >
        <Stack spacing={2}>
          <Box>
            <Typography sx={{ fontWeight: 700, mb: 0.5 }}>
              {config.title}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {config.description}
            </Typography>
            {file && (
              <Typography variant="body2" sx={{ mt: 1, fontWeight: 600 }}>
                Selected file: {file.name}
              </Typography>
            )}
          </Box>

          <input
            ref={fileInputRef}
            type="file"
            hidden
            accept=".xlsx,.xls"
            onChange={handleFileChange}
          />

          <Stack direction={{ xs: "column", sm: "row" }} gap={1}>
            <Button
              className="btn-outlined"
              startIcon={<DownloadOutlined />}
              onClick={handleDownloadTemplate}
              disabled={loading || uploading}
            >
              Download Template
            </Button>
            <Button
              className="btn-outlined"
              startIcon={
                loading ? (
                  <CircularProgress size={18} />
                ) : (
                  <UploadFileOutlined />
                )
              }
              onClick={() => fileInputRef.current?.click()}
              disabled={loading || uploading}
            >
              {loading ? "Processing..." : "Choose Excel"}
            </Button>
            {rows.length > 0 && (
              <Button
                variant="outlined"
                color="error"
                startIcon={<DeleteOutline />}
                onClick={clear}
                disabled={uploading}
              >
                Clear
              </Button>
            )}
            <Box sx={{ flex: 1 }} />
            <Button
              className="btn-contained"
              startIcon={
                uploading ? (
                  <CircularProgress size={18} color="inherit" />
                ) : (
                  <UploadFileOutlined />
                )
              }
              onClick={handleSubmit}
              disabled={uploading || loading || rows.length === 0}
            >
              {uploading ? "Uploading..." : "Submit Report"}
            </Button>
          </Stack>
        </Stack>
      </Paper>

      {rows.length > 0 && (
        <>
          <Stack
            direction="row"
            spacing={1}
            sx={{ mb: 2, justifyContent: "center" }}
          >
            <Chip label={`Rows: ${rows.length}`} variant="outlined" />
            <Chip
              label={`Columns: ${config.columns.length}`}
              variant="outlined"
            />
          </Stack>
          <Paper
            elevation={0}
            sx={{
              border: "1px solid #e3e7ef",
              borderRadius: 2,
              overflow: "hidden",
            }}
          >
            <Box sx={{ p: 2, borderBottom: "1px solid #e3e7ef" }}>
              <Typography sx={{ fontWeight: 700 }}>Preview</Typography>
              <Typography variant="body2" color="text.secondary">
                Review the imported rows before submitting the report.
              </Typography>
            </Box>
            <DataGrid
              rows={rows}
              columns={gridColumns}
              autoHeight
              disableRowSelectionOnClick
              pageSizeOptions={[10, 25, 50, 100]}
              initialState={{
                pagination: { paginationModel: { pageSize: 25, page: 0 } },
              }}
              sx={{
                border: 0,
                "& .MuiDataGrid-columnHeaders": {
                  backgroundColor: "#f7f9fc",
                  fontWeight: 700,
                },
                "& .MuiDataGrid-cell": { fontSize: "0.875rem" },
                "& .MuiDataGrid-row:hover": { backgroundColor: "#fafbfc" },
              }}
            />
          </Paper>
        </>
      )}
    </Box>
  );
}

const UploadReports = ({ reportType = "combined" }) => {
  let config = COMBINED_CONFIG;

  if (reportType === "openPR") {
    config = PR_CONFIG;
  } else if (reportType === "openPO") {
    config = PO_CONFIG;
  } else if (reportType === "delivery") {
    config = DELIVERY_CONFIG;
  }

  return (
    <Box sx={{ py: 3 }}>
      <Box sx={{ mb: 3 }}>
        <Typography
          variant="h4"
          sx={{ fontWeight: 700, color: "#172033", mb: 0.5 }}
        >
          {config.title}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {config.description}
        </Typography>
      </Box>

      <ExcelUploadPanel key={reportType} config={config} />
    </Box>
  );
};

export default UploadReports;
